/**
 * dock.tsx — the macOS-style top dock configuration.
 *
 * Single source of truth for the app's top-level module navigation:
 *   - which modules appear in the dock (DOCK)
 *   - which dock item owns the current route (dockKeyForPath)
 *   - which sidebar sections belong to that item's context (contextSectionsForPath)
 *
 * The dock replaces the old "everything at once" sidebar: the top bar shows
 * the module tiles (colored, Apple-app-like), and the context sidebar below
 * shows only the sections of the active module.
 *
 * Icons are the filled "app icon" style from mockup 01-app-shell-after.html —
 * each module's tile carries its own coloured glyph (no tint box), exactly as
 * Lis approved. The active tile gets the teal tint + glow dot via CSS.
 */

import React from 'react';
import { moduleForPath } from '../../lib/moduleRoutes';
import { DOCK_TILES } from './dockIcons';
import type { ModuleKey } from '../../types';

export type DockKey =
  | 'dashboard'
  | 'crm'
  | 'people'
  | 'performance'
  | 'work'
  | 'support'
  | 'communication'
  | 'analytics'
  | 'marketing'
  | 'finance'
  | 'admin';

export interface DockItem {
  key: DockKey;
  /** i18n key for the tile label / tooltip. */
  labelKey: string;
  /** Fallback label used when no translation exists (i18next defaultValue). */
  fallback: string;
  /** Icon tile tint (Apple-style app-icon color). */
  color: string;
  /**
   * Small monochrome glyph (currentColor) — the SIDEBAR surfaces: the active-
   * module chip and the mobile module switcher, where the icon sits inside a
   * container that supplies the colour. NOT what the top dock renders.
   */
  icon: React.ReactNode;
  /**
   * The top dock's tile — the custom Claude Design app icon. A complete
   * 48×48 artwork that draws its own rounded face, border and glyph
   * (src/assets/dock-icons/, inlined via dockIcons.tsx), so the dock button
   * renders it bare: no background, border or gloss of its own.
   */
  tile: React.ReactNode;
  /** NAV section labels shown in the context sidebar while this item is active. */
  sections: string[];
  /** Route prefixes that activate this item where moduleForPath returns null. */
  prefixes?: string[];
  /** ModuleKeys (from moduleForPath) that map onto this dock item. */
  modules?: ModuleKey[];
  /** Fallback landing path when the icon is clicked. */
  defaultPath: string;
}

/* ── App-icon glyphs ──────────────────────────────────────────────────────────
   Monochrome white glyphs (every stroke/fill = currentColor). The COLOUR of a
   tile comes from its body, not the glyph: on the desktop dock the tile is a
   filled colour app-icon and `color` is white, so these render white-on-colour;
   on the mobile switcher + sidebar chip the container sets `color: item.color`,
   so the SAME glyph renders in the module's colour. One glyph, three surfaces.
   Everything is inline SVG — no icon-name lookup can fail (item 3). */

const IconDashboard = (
  <svg viewBox="0 0 24 24" fill="none">
    <rect x="3" y="3" width="7.5" height="7.5" rx="2" fill="currentColor" />
    <rect x="13.5" y="3" width="7.5" height="7.5" rx="2" fill="currentColor" opacity=".55" />
    <rect x="3" y="13.5" width="7.5" height="7.5" rx="2" fill="currentColor" opacity=".55" />
    <rect x="13.5" y="13.5" width="7.5" height="7.5" rx="2" fill="currentColor" opacity=".85" />
  </svg>
);

/* CRM — a contact card (frame + person + rows). */
const IconCrm = (
  <svg viewBox="0 0 24 24" fill="none">
    <rect x="3" y="4.5" width="18" height="15" rx="3" fill="currentColor" opacity=".28" />
    <circle cx="9" cy="10.5" r="2.5" fill="currentColor" />
    <path d="M5.3 16.5a3.7 3.7 0 0 1 7.4 0Z" fill="currentColor" />
    <rect x="14" y="8.6" width="5.2" height="1.9" rx=".95" fill="currentColor" />
    <rect x="14" y="12.4" width="5.2" height="1.9" rx=".95" fill="currentColor" opacity=".7" />
  </svg>
);

/* People — two heads + shoulders. */
const IconPeople = (
  <svg viewBox="0 0 24 24" fill="none">
    <circle cx="9.3" cy="8.4" r="3.3" fill="currentColor" />
    <circle cx="16.6" cy="9.8" r="2.5" fill="currentColor" opacity=".7" />
    <path d="M3.2 19a6.1 6.1 0 0 1 12.2 0Z" fill="currentColor" />
    <path d="M14.6 19a5.2 5.2 0 0 1 6.2-5.1V19Z" fill="currentColor" opacity=".7" />
  </svg>
);

/* Finance — a banknote with a coin (dark cutouts read on white + coloured). */
const IconFinance = (
  <svg viewBox="0 0 24 24" fill="none">
    <rect x="3" y="6" width="18" height="12.5" rx="2.6" fill="currentColor" />
    <path d="M3 9.6h18" stroke="rgba(0,0,0,.28)" strokeWidth={1.5} />
    <circle cx="12" cy="12.4" r="2.4" fill="rgba(0,0,0,.22)" />
  </svg>
);

/* Work — a briefcase. */
const IconWork = (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M9 7V5.6a3 3 0 0 1 6 0V7" stroke="currentColor" strokeWidth={2} />
    <rect x="3.5" y="7" width="17" height="12.5" rx="3" fill="currentColor" />
    <rect x="9.4" y="11.2" width="5.2" height="2.4" rx="1.2" fill="currentColor" opacity=".35" />
  </svg>
);

/* Support — a lifebuoy: a thick ring (centre shows the tile colour = a real
   hole) + hub + spokes, all in the glyph colour so it reads on every surface. */
const IconSupport = (
  <svg viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="7.6" stroke="currentColor" strokeWidth={3.2} />
    <path d="M12 4.4v3.3M12 16.3v3.3M4.4 12h3.3M16.3 12h3.3M6.5 6.5l2.3 2.3M15.2 15.2l2.3 2.3M17.5 6.5l-2.3 2.3M8.8 15.2l-2.3 2.3" stroke="currentColor" strokeWidth={3} strokeLinecap="round" />
    <circle cx="12" cy="12" r="2.3" fill="currentColor" />
  </svg>
);

/* Communication — an envelope (dark flap reads on white + coloured bodies). */
const IconCommunication = (
  <svg viewBox="0 0 24 24" fill="none">
    <rect x="3" y="5.5" width="18" height="13" rx="3.2" fill="currentColor" />
    <path d="M4.6 8.4l7.4 5.1 7.4-5.1" fill="none" stroke="rgba(0,0,0,.3)" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* Marketing — a megaphone. */
const IconMarketing = (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M3 10a1.6 1.6 0 0 1 1.6-1.6H7l8.5-4.2v15.6L7 15.6H4.6A1.6 1.6 0 0 1 3 14Z" fill="currentColor" />
    <path d="M17.5 9c1.9.5 3.2 1.6 3.2 3s-1.3 2.5-3.2 3" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" opacity=".7" />
    <path d="M7 15.6V19a1.6 1.6 0 0 0 3.1.5L9 15.6Z" fill="currentColor" opacity=".7" />
  </svg>
);

/* Analytics — ascending bars. */
const IconAnalytics = (
  <svg viewBox="0 0 24 24" fill="none">
    <rect x="4" y="12" width="4" height="8" rx="1.3" fill="currentColor" opacity=".6" />
    <rect x="10" y="7" width="4" height="13" rx="1.3" fill="currentColor" opacity=".8" />
    <rect x="16" y="3" width="4" height="17" rx="1.3" fill="currentColor" />
  </svg>
);

/* Performance — a gauge. */
const IconPerformance = (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M4.4 17.5a8.5 8.5 0 1 1 15.2 0Z" fill="currentColor" opacity=".3" />
    <path d="M4.4 17.5a8.5 8.5 0 1 1 15.2 0" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
    <path d="M12 12.5l4-3.2" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
    <circle cx="12" cy="12.6" r="1.9" fill="currentColor" />
  </svg>
);

/* Administration — a building (facilities / procurement / assets / vendors).
   NOT a gear/sun: at dock size the old thin radial gear read as a second
   theme-toggle sitting beside the sun/moon (Lis's screenshot). A filled
   building, in its own colour, reads unambiguously as a launcher tile. */
const IconAdmin = (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M4 20V6.5a1.6 1.6 0 0 1 1-1.5l6-2.4a1.2 1.2 0 0 1 1.6 1.1V20Z" fill="currentColor" />
    <path d="M12.6 20V9.2l6 2.1a1.6 1.6 0 0 1 1.1 1.5V20Z" fill="currentColor" opacity=".7" />
    <rect x="6" y="7.6" width="1.6" height="1.6" rx=".4" fill="currentColor" opacity=".28" />
    <rect x="9" y="7.6" width="1.6" height="1.6" rx=".4" fill="currentColor" opacity=".28" />
    <rect x="6" y="10.8" width="1.6" height="1.6" rx=".4" fill="currentColor" opacity=".28" />
    <rect x="9" y="10.8" width="1.6" height="1.6" rx=".4" fill="currentColor" opacity=".28" />
    <rect x="6" y="14" width="1.6" height="1.6" rx=".4" fill="currentColor" opacity=".28" />
    <rect x="9" y="14" width="1.6" height="1.6" rx=".4" fill="currentColor" opacity=".28" />
  </svg>
);

/** Mockup 01 tile order: logo, Dashboard → Analytics, then Admin in utilities. */
export const DOCK: DockItem[] = [
  {
    key: 'dashboard',
    labelKey: 'nav.dashboard',
    fallback: 'Dashboard',
    color: '#2DD4BF',
    icon: IconDashboard,
    tile: DOCK_TILES['dashboard'],
    sections: ['nav.dashboard'],
    prefixes: ['/dashboard', '/profile', '/notifications', '/users'],
    defaultPath: '/dashboard',
  },
  {
    key: 'crm',
    labelKey: 'nav.crm',
    fallback: 'CRM',
    color: '#5EA9F0',
    icon: IconCrm,
    tile: DOCK_TILES['crm'],
    sections: ['nav.crm'],
    modules: ['crm'],
    defaultPath: '/crm/dashboard',
  },
  {
    key: 'people',
    labelKey: 'nav.people',
    fallback: 'People & HR',
    color: '#A78BFA',
    icon: IconPeople,
    tile: DOCK_TILES['hr'],
    sections: ['nav.people', 'nav.hr'],
    modules: ['hr', 'leave', 'recruitment'],
    defaultPath: '/hr/my-hr',
  },
  {
    key: 'performance',
    labelKey: 'nav.performance',
    fallback: 'Performance',
    color: '#22D3EE',
    icon: IconPerformance,
    tile: DOCK_TILES['performance'],
    sections: ['nav.performance'],
    modules: ['performance'],
    defaultPath: '/performance/dashboard',
  },
  {
    key: 'finance',
    labelKey: 'nav.finance',
    fallback: 'Finance',
    color: '#F4B740',
    icon: IconFinance,
    tile: DOCK_TILES['finance'],
    sections: ['nav.finance'],
    modules: ['finance'],
    defaultPath: '/finance/expenses',
  },
  {
    key: 'work',
    labelKey: 'nav.work',
    fallback: 'Work',
    color: '#4ADE9E',
    icon: IconWork,
    tile: DOCK_TILES['tasks'],
    sections: ['nav.work'],
    modules: ['projects'],
    defaultPath: '/work/my-tasks',
  },
  {
    key: 'support',
    labelKey: 'nav.support',
    fallback: 'Support',
    color: '#FB7185',
    icon: IconSupport,
    tile: DOCK_TILES['support'],
    sections: ['nav.support'],
    modules: ['support'],
    defaultPath: '/support/my-tickets',
  },
  {
    key: 'communication',
    labelKey: 'nav.communication',
    fallback: 'Communication',
    color: '#60A5FA',
    icon: IconCommunication,
    tile: DOCK_TILES['communication'],
    sections: ['nav.company'],
    /* /company/* is deliberately unmapped in moduleRoutes (cross-cutting content —
       a company that bought only CRM still has announcements and a directory), so
       without an explicit prefix NO tile owned Announcements, Policies, Events or
       Directory (PR #38 fix, kept). */
    prefixes: ['/company'],
    modules: ['communication'],
    defaultPath: '/communication/channels',
  },
  {
    key: 'marketing',
    labelKey: 'nav.marketing',
    fallback: 'Marketing',
    color: '#F472B6',
    icon: IconMarketing,
    tile: DOCK_TILES['marketing'],
    sections: ['nav.marketing'],
    modules: ['marketing'],
    defaultPath: '/marketing/dashboard',
  },
  {
    key: 'analytics',
    labelKey: 'nav.analytics',
    fallback: 'Analytics',
    color: '#22D3EE',
    icon: IconAnalytics,
    tile: DOCK_TILES['analytics'],
    sections: ['nav.analytics'],
    modules: ['analytics'],
    defaultPath: '/analytics/overview',
  },
  {
    key: 'admin',
    labelKey: 'nav.admin',
    fallback: 'Admin',
    /* Distinct indigo — a coloured launcher tile like the others, NOT the neutral
       slate that made it read as a utility control beside the theme toggle. */
    color: '#818CF8',
    icon: IconAdmin,
    tile: DOCK_TILES['owner'],
    sections: ['nav.administration', 'nav.owner', 'nav.admin'],
    prefixes: ['/admin', '/settings'],
    /* No `modules` association — deliberate. moduleRoutes.ts declares most of
       /admin/* cross-cutting and NOT module-gated; listing one here made the
       dock filter hide the whole Admin tile when that module was off (PR #67
       regression, Lis lost the tile). The /admin/assets page gates itself. */
    defaultPath: '/admin/holidays',
  },
];

/** ModuleKey → dock tile. */
/**
 * ⚠️ `Partial` SINCE 2026-10-04, AND THE GAP IS DELIBERATE. `mail` became a ModuleKey
 * with migration 0052, and it has **no dock tile** — Webmail lives in the Apps group, not
 * in the module dock. The honest shape is a map with a hole in it rather than a total map
 * with an invented entry: pointing `mail` at `communication` (the nearest-looking tile)
 * would light up the wrong dock item on every mail route.
 *
 * The single call site already reads `if (key) return key;` and falls through to the
 * prefix scan, so a missing entry was always handled — the type was the only thing
 * claiming otherwise.
 */
const MODULE_TO_DOCK: Partial<Record<ModuleKey, DockKey>> = {
  hr: 'people',
  leave: 'people',
  recruitment: 'people',
  projects: 'work',
  support: 'support',
  communication: 'communication',
  analytics: 'analytics',
  crm: 'crm',
  marketing: 'marketing',
  finance: 'finance',
  performance: 'performance',
  assets: 'admin',
  time: 'people',
};

/** The dock tile that owns `pathname`, or null (e.g. /login). */
export function dockKeyForPath(pathname: string): DockKey | null {
  const module = moduleForPath(pathname);
  if (module) {
    const key = MODULE_TO_DOCK[module];
    if (key) return key;
  }
  const path = `/${pathname.replace(/^\/+/, '')}`;
  for (const item of DOCK) {
    if (item.prefixes?.some(p => path === p || path.startsWith(`${p}/`))) {
      return item.key;
    }
  }
  return null;
}

/** NAV section labels that belong to the context of the active route. */
export function contextSectionsForPath(pathname: string): string[] {
  const key = dockKeyForPath(pathname);
  if (!key) return [];
  return DOCK.find(d => d.key === key)?.sections ?? [];
}

/** Dock item for a key. */
export function dockItem(key: DockKey): DockItem {
  return DOCK.find(d => d.key === key) as DockItem;
}
